import { useState, useEffect, useCallback, useRef } from "react";
import { RevealTextStripContent } from "../../../../../types/newtypes";

interface UseRevealTextStripHookProps {
  content: RevealTextStripContent;
  onCardStateChange?: (isCompleted: boolean, canProceed: boolean) => void;
}

interface UseRevealTextStripHookReturn {
  selectedItemIndex: number | null;
  handleItemReveal: (index: number) => void;
  allItemsRevealed: boolean;
  remainingItems: number;
  completedItems: Set<number>;
  // Scroll-related properties
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  showScrollIndicators: boolean;
  canScrollLeft: boolean;
  canScrollRight: boolean;
  handleScroll: () => void;
  scrollLeft: () => void;
  scrollRight: () => void;
}

export const useRevealTextStripHook = ({
  content,
  onCardStateChange,
}: UseRevealTextStripHookProps): UseRevealTextStripHookReturn => {
  const [revealedItems, setRevealedItems] = useState<Set<number>>(new Set());
  const [selectedItemIndex, setSelectedItemIndex] = useState<number | null>(
    null,
  );
  const [isInitialized, setIsInitialized] = useState(false);
  const [completedItems, setCompletedItems] = useState<Set<number>>(new Set());

  // Scroll-related state
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollIndicators, setShowScrollIndicators] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Check if all items have been revealed
  const allItemsRevealed = revealedItems?.size === content?.items?.length;

  // Set initialized after first render
  useEffect(() => {
    setIsInitialized(true);
  }, []);

  // Preload images when content changes
  useEffect(() => {
    if (content?.items) {
      content.items.forEach((item) => {
        const imageUri = item?.image_uri || item?.icon_uri;
        if (imageUri) {
          const img = new Image();
          img.src = imageUri;
        }
      });
    }
  }, [content?.items]);

  // Update card state when all items are revealed (only after initialization)
  useEffect(() => {
    if (isInitialized && onCardStateChange) {
      if (allItemsRevealed) {
        onCardStateChange(true, true);
      } else {
        onCardStateChange(false, false);
      }
    }
  }, [allItemsRevealed, onCardStateChange, isInitialized]);

  // Calculate remaining items
  const remainingItems = content?.items?.length - revealedItems?.size;

  // Check if scrolling is needed
  useEffect(() => {
    const checkScroll = () => {
      if (scrollContainerRef?.current) {
        const { scrollLeft, scrollWidth, clientWidth } =
          scrollContainerRef.current;
        setShowScrollIndicators(scrollWidth > clientWidth);
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
      }
    };

    checkScroll();
    window?.addEventListener("resize", checkScroll);
    return () => window?.removeEventListener("resize", checkScroll);
  }, [content?.items?.length]);

  // Handle scroll
  const handleScroll = useCallback(() => {
    if (scrollContainerRef?.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  }, []);

  // Calculate scroll amount based on card width
  const getScrollAmount = useCallback(() => {
    if (scrollContainerRef?.current) {
      const container = scrollContainerRef.current;
      const firstCard = container.querySelector("button");
      if (firstCard) {
        return firstCard.offsetWidth + 16;
      }
    }
    return 200; // fallback
  }, []);

  // Scroll functions
  const scrollLeft = useCallback(() => {
    if (scrollContainerRef?.current) {
      const scrollAmount = getScrollAmount();
      scrollContainerRef.current?.scrollBy({
        left: -scrollAmount,
        behavior: "smooth",
      });
    }
  }, [getScrollAmount]);

  const scrollRight = useCallback(() => {
    if (scrollContainerRef?.current) {
      const scrollAmount = getScrollAmount();
      scrollContainerRef.current?.scrollBy({
        left: scrollAmount,
        behavior: "smooth",
      });
    }
  }, [getScrollAmount]);

  // Handle item reveal (track all revealed items, but only one selected at a time)
  const handleItemReveal = useCallback(
    (index: number) => {
      setRevealedItems((prev) => {
        const newSet = new Set(prev);
        newSet.add(index);
        setCompletedItems((prevCompleted) => {
          const updated = new Set(prevCompleted);
          updated.add(index);
          return updated;
        });

        if (selectedItemIndex === index) {
          setSelectedItemIndex(null);
        } else {
          setSelectedItemIndex(index);
        }

        return newSet;
      });
    },
    [selectedItemIndex],
  );

  return {
    selectedItemIndex,
    handleItemReveal,
    allItemsRevealed,
    remainingItems,
    completedItems,
    scrollContainerRef,
    showScrollIndicators,
    canScrollLeft,
    canScrollRight,
    handleScroll,
    scrollLeft,
    scrollRight,
  };
};
